import { reCAPTCHAv3 } from "commonAssets/js/constants";

/**
 * reCAPTCHA action for class member logins. The getClassMember lambda accepts "login" and
 * "submit" tokens, and rejects any lookup (notice ID + confirmation code, or purl) without one.
 * @constant {string}
 */
export const LOGIN_RECAPTCHA_ACTION = "login";

/**
 * Gets a fresh reCAPTCHA Enterprise token for a class member login. Tokens are single use and
 * expire after two minutes, so one is requested for every lookup.
 * @returns {Promise<string>} the token
 */
export function getLoginRecaptchaToken() {
    return new Promise((resolve, reject) => {
        const enterprise = window.grecaptcha?.enterprise;
        if (!enterprise || typeof enterprise.ready !== "function") {
            reject(new Error("reCAPTCHA did not load"));
            return;
        }
        enterprise.ready(() => {
            enterprise
                .execute(reCAPTCHAv3, { action: LOGIN_RECAPTCHA_ACTION })
                .then(resolve, reject);
        });
    });
}

/**
 * Makes every classMemberPost on this API client carry a login reCAPTCHA token in `body.token`.
 * Applied once per client in the site's form constructors, so it also covers the login in the
 * inherited common SubmitClaim.edit_claim without changing common code. If a token cannot be
 * obtained the returned promise rejects, and the callers' existing .catch shows the error modal.
 * @param {Object} api - an apigClient instance
 * @returns {Object} the same client
 */
export function withLoginRecaptcha(api) {
    if (
        !api ||
        typeof api.classMemberPost !== "function" ||
        api.classMemberPost.withLoginRecaptcha
    ) {
        return api;
    }
    const classMemberPost = api.classMemberPost;
    const wrapped = (params, body, additionalParams) =>
        getLoginRecaptchaToken().then((token) =>
            classMemberPost.call(
                api,
                params,
                { ...body, token },
                additionalParams
            )
        );
    wrapped.withLoginRecaptcha = true;
    api.classMemberPost = wrapped;
    return api;
}
